<template>
  <div
    class="a-cover lg:w-96 overflow-y-auto bg-white transition"
    :class="{ 'sidebar-closed': !sidebarOpen }"
  >
    <div class="flex-a justify-between p-x h-10 md:h-12 bg-grey-2">
      <BtnBack
        icon="BackIcon"
        aria="Back"
        text="Back"
        iconsize="1.5rem"
        textsize="base"
        iconcolor="primary"
        textcolor="primary"
        class="font-bold"
      />
      <Button
        icon="BackIcon"
        type="button"
        iconsize="1.5rem"
        iconcolor="primary"
        data-gtm-sidebar-toggle
        :aria="sidebarOpen ? 'Hide sidebar' : 'Show sidebar'"
        :aria-expanded="sidebarOpen || false"
        aria-controls="sidebar-content"
        :iconrotation="sidebarOpen ? 'rotate-270' : 'rotate-90'"
        class="h-6"
        @buttonClick="sidebarOpen = !sidebarOpen"
      />
    </div>
    <h1
      id="focus-target"
      class="flex-a h-8 md:h-12 p-x focus-b"
      :class="[`bg-${palette.primary}`, { 'text-white': isGuided }]"
      v-html="location.title"
    />

    <ul v-if="isGuided" class="flex-a h-12 p-x bg-grey-2">
      <li>
        <Button
          :path="pager.prev"
          icon="BackIcon"
          iconsize="1.5rem"
          iconcolor="primary"
          text="Prev"
          :iconleft="true"
          aria="Previous panorama"
          iconrotation=""
          class="h-6 font-bold mr-8"
        />
      </li>
      <li>
        <Button
          :path="pager.next"
          icon="BackIcon"
          iconsize="1.5rem"
          iconcolor="primary"
          text="Next"
          :iconright="true"
          aria="Next panorama"
          iconrotation="rotate-180"
          class="h-6 font-bold"
        />
      </li>
    </ul>

    <section
      id="sidebar-content"
      :aria-label="`${htmlDecode(location.title)}`"
      :aria-hidden="!sidebarOpen"
      class="transition"
      :class="{ hidden: !sidebarOpen }"
    >
      <SidebarItem
        v-if="isGuided"
        :post="post"
        :expanded="{ open: true }"
        :category="category"
        :palette="palette"
        @handleFocus="handleFocus"
        @handleBlur="handleBlur"
        @handleEnter="handleEnter"
        @handleEscape="handleEscape"
        @handleClick="handleClick"
      />

      <nav v-else aria-label="Panoramas and content spots">
        <ul>
          <li v-for="(child, i) in location.children" :key="child.slug">
            <SidebarItem
              :post="child"
              :expanded="{
                open: openTab === i,
                index: i,
                openIndex: openTab
              }"
              :category="category"
              :palette="palette"
              @handleFocus="handleFocus"
              @handleBlur="handleBlur"
              @handleEnter="handleEnter"
              @handleEscape="handleEscape"
              @handleClick="handleClick"
            />
          </li>
        </ul>
      </nav>

      <blockquote v-if="isGuided && post.guided_tour" class="p-x py-8">
        <p class="relative mb-5 leading-normal font-serif">
          <svg xmlns="http://www.w3.org/2000/svg" width="37" height="32" viewBox="0 0 37 32" v-bind:svg-inline="''" v-bind:class="'absolute top-0 -mt-4 -ml-4 h-10 w-10'" v-bind:role="'presentation'" v-bind:focusable="'false'" v-bind:tabindex="'-1'"><path fill="#e3e3e3" d="M19.697 11.793a10.1 10.1 0 00-7.964 2.586l.008-.007c2.137-4.326 6.354-7.338 11.31-7.735l.049-.003.029-.251c-7.427 0-13.998 4.642-14.834 11.86-.056.453-.088.978-.088 1.51 0 2.053.476 3.995 1.324 5.721l-.034-.076c.112.241.236.477.365.708 1.329 2.273 3.476 3.946 6.028 4.628l.069.016c.042.011.085.02.128.03.385.102.854.189 1.333.244l.047.004c.361.047.778.073 1.202.073 5.012 0 9.155-3.724 9.811-8.556l.006-.051a9.51 9.51 0 00.051-.993c0-5.064-3.862-9.227-8.802-9.703l-.04-.003z"/></svg>
          <span class="relative z-1" v-html="post.quote.copy" />
          <span class="relative inline-block">
            <svg xmlns="http://www.w3.org/2000/svg" width="37" height="32" viewBox="0 0 37 32" v-bind:svg-inline="''" v-bind:class="'absolute bottom-0 -mb-6 -ml-6 h-10 w-10 rotate-180'" v-bind:role="'presentation'" v-bind:focusable="'false'" v-bind:tabindex="'-1'"><path fill="#e3e3e3" d="M19.697 11.793a10.1 10.1 0 00-7.964 2.586l.008-.007c2.137-4.326 6.354-7.338 11.31-7.735l.049-.003.029-.251c-7.427 0-13.998 4.642-14.834 11.86-.056.453-.088.978-.088 1.51 0 2.053.476 3.995 1.324 5.721l-.034-.076c.112.241.236.477.365.708 1.329 2.273 3.476 3.946 6.028 4.628l.069.016c.042.011.085.02.128.03.385.102.854.189 1.333.244l.047.004c.361.047.778.073 1.202.073 5.012 0 9.155-3.724 9.811-8.556l.006-.051a9.51 9.51 0 00.051-.993c0-5.064-3.862-9.227-8.802-9.703l-.04-.003z"/></svg>
          </span>
        </p>
        <footer class="font-bold">
          <span
            class="inline-block mb-1 border-b border-gold leading-none"
            v-html="post.quote.source"
          />
          <br />
          <span
            class="inline-block border-b border-gold leading-none"
            v-html="post.quote.qualification"
          />
        </footer>
      </blockquote>
    </section>
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import { bus } from '@/main'
import Button from '@/components/buttons/Button.vue'

import SidebarItem from '@/components/SidebarItem.vue'
import BtnBack from '@/components/buttons/BtnBack.vue'

export default {
  name: 'Sidebar',

  components: {
    SidebarItem,
    BtnBack,
    Button
  },

  props: {
    post: {
      type: Object,
      default: () => {}
    },
    location: {
      type: Object,
      default: () => {}
    },
    category: {
      type: Object,
      default: () => {}
    },
    palette: {
      type: Object,
      default: () => {}
    },
    isGuided: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      sidebarOpen: true
    }
  },

  computed: {
    ...mapState(['touch', 'mobile', 'browser']),
    ...mapGetters([]),

    openTab() {
      // Return the index of the open sidebar item
      return this.location.children.findIndex(
        item => item.slug === this.post.slug
      )
    },

    pager() {
      const tourGroup = this.location.children

      const tourGroupLength = tourGroup.length

      const tourGroupSlugs = tourGroup.map(({ slug }) => slug)

      const currentPanorama = this.$route.params.panorama_id

      const currentIndex = tourGroupSlugs.indexOf(currentPanorama)

      const prev =
        currentIndex > 0
          ? tourGroupSlugs[currentIndex - 1]
          : tourGroupSlugs[tourGroupLength - 1]

      const next =
        currentIndex < tourGroupLength - 1
          ? tourGroupSlugs[currentIndex + 1]
          : tourGroupSlugs[0]

      return { prev, next, currentIndex, currentPanorama, tourGroupSlugs }
    }
  },

  watch: {
    sidebarOpen: {
      immediate: true,
      handler(value) {
        this.$store.commit('toggleSidebarState', value)
      }
    }
  },

  created() {
    if (this.mobile) {
      this.sidebarOpen = false
    }
  },

  methods: {
    handleFocus(slug) {
      // Event bus direct to Panorama.vue
      bus.$emit('panToHotSpot', slug)
    },

    handleBlur(slug) {
      // Event bus direct to Panorama.vue
      bus.$emit('closeHotSpot', slug)
    },

    handleEnter(slug) {
      // Emits to the parent Viewer.vue
      this.$emit('hotSpotClick', slug)
    },

    handleEscape(slug) {
      // Event bus direct to Panorama.vue
      bus.$emit('closeHotSpot', slug)
    },

    handleClick(slug) {
      if (this.mobile) {
        this.$emit('hotSpotClick', slug)
      }

      // Because Safari doen't 'focus' a button on click
      if (!this.mobile && this.browser === 'Safari') {
        this.handleFocus(slug)
      }
    }
  }
}
</script>
